import type {
  QuizQuestionInputConfig,
  QuizQuestionInputType,
  QuizQuestionInputValidationRules,
  QuizQuestionChoice,
  QuizFormComponent,
} from "../quiz.types";

type ComponentAndProps = {
  component: QuizFormComponent;
  options?: Array<{ label: string; value: number; description?: string }>;
  displayType?: string;
  maxItems?: number;
  isCustomComponent?: boolean;
  isForceYes?: boolean;
  placeholder?: string;
  // File-based answer props
  questionType?: "image" | "file";
  maxFiles?: number;
  // HTML input type (for Input component)
  inputType?: string;
  // Longtext-specific props
  showCharacterCount?: boolean;
  showWordCount?: boolean;
  maxWords?: number;
  maxCharacters?: number;
  minCharacters?: number;
};

/**
 * Maps a quiz question type to its corresponding form component and props.
 * Determines the appropriate UI component based on question configuration.
 */
export function mapQuestionTypeToComponentAndProps(
  choices: QuizQuestionChoice[],
  type: QuizQuestionInputType,
  config_object: QuizQuestionInputConfig | null | undefined,
  validation_rules: QuizQuestionInputValidationRules | null | undefined,
  maximum_answers?: number
): ComponentAndProps {
  const config = config_object ?? ({} as QuizQuestionInputConfig);
  const rules = validation_rules ?? ({} as QuizQuestionInputValidationRules);
  let component: QuizFormComponent = "Input";

  let props: Omit<ComponentAndProps, "component"> = {};

  const options = choices.map((choice) => ({
    label: choice.display_text,
    value: choice.id,
    description: choice.display_description,
  }));

  switch (type) {
    case "yes/no":
      component = "YesNoButtons";

      props = {
        ...props,
        options,
        isCustomComponent: true,
      };

      if (config.force_yes) {
        component = "ForceYesInput";

        props = {
          ...props,
          isForceYes: true,
        };
      }
      break;
    case "text input":
      component = "Input";
      props = {
        ...props,
        placeholder: config.placeholder || "Enter your answer here",
      };
      break;
    case "url":
      component = "Input";
      props = {
        ...props,
        placeholder: config.placeholder || "https://example.com",
        inputType: "url",
      };
      break;
    case "longtext":
      component = "Textarea";
      props = {
        ...props,
        placeholder:
          config.placeholder || "Enter your response here.",
        showCharacterCount: config.show_character_count,
        showWordCount: config.show_word_count,
        maxWords: config.max_words,
        maxCharacters: rules.max_characters,
        minCharacters: rules.min_characters,
      };
      break;
    case "multiple choice":
      if (
        config.display_type === "select" ||
        config.display_type === "cloud"
      ) {
        component =
          config.display_type === "select" ? "Select" : "SelectChips";
      } else {
        component = "RadioGroup";
      }

      props = {
        ...props,
        options,
        displayType: config.display_type,
        maxItems:
          rules.max_select === "none"
            ? Infinity
            : rules.max_select,
        isCustomComponent: true,
      };
      break;
    case "organization selector":
      component = "AffiliationListContainer";
      props = {
        ...props,
        isCustomComponent: true,
        maxItems:
          rules.max_select === "none"
            ? Infinity
            : rules.max_select,
      };
      break;
    case "image": {
      // Calculate effective max files
      const effectiveMaxFiles =
        rules.max_files ??
        maximum_answers ??
        (rules.max_select === "none"
          ? undefined
          : rules.max_select) ??
        1;

      // Use ImageField for single image uploads, FileAnswerInput for multiple
      if (effectiveMaxFiles === 1) {
        component = "ImageField";
        props = {
          ...props,
          isCustomComponent: true,
        };
      } else {
        component = "FileAnswerInput";
        props = {
          ...props,
          questionType: "image",
          maxFiles: effectiveMaxFiles,
          isCustomComponent: true,
        };
      }
      break;
    }
    case "file":
      component = "FileAnswerInput";
      props = {
        ...props,
        questionType: "file",
        maxFiles:
          rules.max_files ??
          maximum_answers ??
          (rules.max_select === "none"
            ? undefined
            : rules.max_select) ??
          1, // Default to 1 file if not specified
        isCustomComponent: true,
      };
      break;
  }

  return { component, ...props };
}

